// Model pricing (rate card) admin screen. Lists every priced model and lets an
// admin add a new model or correct an existing rate. Rates are USD per
// 1,000,000 tokens; which bucket keys apply depends on the channel (contract
// §3.5). Edits go live within ~10 min (the app caches the rate card).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CheckCircle2, Pencil, Plus, X } from "lucide-react";
import {
  getCostPricing,
  upsertCostPricing,
} from "../../../Services/adminReporting";
import { Skeleton } from "../../../ui/skeleton";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Switch } from "../../../ui/switch";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Panel } from "../../../Components/admin/reporting/controls";
import { ChannelBadge, fmtDateTime } from "./costFormat";

// Rate bucket keys per channel + a friendly label for each.
const RATE_BUCKETS = {
  chat: [
    ["input", "Input"],
    ["cached_input", "Cached input"],
    ["output", "Output"],
  ],
  voice: [
    ["text_input", "Text input"],
    ["cached_text_input", "Cached text input"],
    ["audio_input", "Audio input"],
    ["cached_audio_input", "Cached audio input"],
    ["text_output", "Text output"],
    ["audio_output", "Audio output"],
  ],
};

const fmtRate = (v) => (v == null ? "—" : `$${Number(v)}`);

const blankRates = (channel) =>
  Object.fromEntries(RATE_BUCKETS[channel].map(([key]) => [key, ""]));

const PricingDialog = ({ open, row, onClose, onSaved }) => {
  const editing = !!row;
  const [model, setModel] = useState("");
  const [channel, setChannel] = useState("chat");
  const [rates, setRates] = useState(() => blankRates("chat"));
  const [verified, setVerified] = useState(false);
  const [active, setActive] = useState(true);
  const [notes, setNotes] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Seed the form when (re)opened, from the edited row or fresh defaults.
  useEffect(() => {
    if (!open) return;
    const ch = row?.channel || "chat";
    setModel(row?.model || "");
    setChannel(ch);
    setRates({
      ...blankRates(ch),
      ...Object.fromEntries(
        Object.entries(row?.rates || {}).map(([k, v]) => [k, String(v)])
      ),
    });
    setVerified(row?.verified ?? false);
    setActive(row?.active ?? true);
    setNotes(row?.notes || "");
    setSourceUrl(row?.source_url || "");
    setEffectiveDate(row?.effective_date ? row.effective_date.slice(0, 10) : "");
    setError(null);
  }, [open, row]);

  // Switching channel resets the rate fields to that channel's buckets.
  const onChannelChange = (next) => {
    setChannel(next);
    setRates(blankRates(next));
  };

  const setRate = (key, value) =>
    setRates((r) => ({ ...r, [key]: value }));

  const submit = async () => {
    if (!model.trim()) {
      setError("Model id is required.");
      return;
    }
    const numericRates = {};
    for (const [key] of RATE_BUCKETS[channel]) {
      const raw = rates[key];
      if (raw === "" || raw == null) continue;
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) {
        setError(`Rate "${key}" must be a non-negative number.`);
        return;
      }
      numericRates[key] = n;
    }
    if (Object.keys(numericRates).length === 0) {
      setError("Enter at least one rate.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        model: model.trim(),
        channel,
        rates: numericRates,
        verified,
        active,
      };
      if (notes.trim()) body.notes = notes.trim();
      if (sourceUrl.trim()) body.source_url = sourceUrl.trim();
      if (effectiveDate) body.effective_date = effectiveDate;
      await upsertCostPricing(body);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save pricing.");
    } finally {
      setSaving(false);
    }
  };

  return (
    // Flex-overlay centering (matching DemographicsPopover) — immune to the
    // ancestor-transform bug that shoves the translate-centered shadcn dialog
    // into the viewport corner.
    <DialogPrimitive.Root open={open} onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) onClose();
          }}
        >
          <DialogPrimitive.Content
            className="relative flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border bg-background shadow-2xl data-[state=open]:animate-in data-[state=open]:zoom-in-95"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="border-b px-6 py-4">
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                {editing ? "Edit model rate" : "Add model"}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1.5 text-sm text-muted-foreground">
                Rates are USD per 1,000,000 tokens. Changes go live within ~10
                minutes.
              </DialogPrimitive.Description>
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cost-model">Model (id or prefix)</Label>
              <Input
                id="cost-model"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="gpt-5-mini"
                disabled={editing}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="cost-channel">Channel</Label>
              <select
                id="cost-channel"
                value={channel}
                onChange={(e) => onChannelChange(e.target.value)}
                disabled={editing}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
              >
                <option value="chat">chat</option>
                <option value="voice">voice</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Rates · USD / 1M tokens
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {RATE_BUCKETS[channel].map(([key, label]) => (
                <div key={key}>
                  <Label htmlFor={`rate-${key}`} className="text-xs font-normal">
                    {label}
                  </Label>
                  <Input
                    id={`rate-${key}`}
                    type="number"
                    min="0"
                    step="0.001"
                    value={rates[key] ?? ""}
                    onChange={(e) => setRate(key, e.target.value)}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cost-effective">Effective date</Label>
              <Input
                id="cost-effective"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="cost-source">Source URL</Label>
              <Input
                id="cost-source"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="cost-notes">Notes</Label>
            <Input
              id="cost-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Confirmed on billing dashboard…"
              className="mt-1"
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Verified against billing
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Active
            </label>
          </div>

              {error ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  {error}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 border-t px-6 py-3">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Add model"}
              </Button>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

const PricingTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null); // {row} | {row:null} | null
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCostPricing());
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load pricing.");
    } finally {
      setLoading(false);
    }
  }, []);

  // One-click activate/deactivate from the table. PUT requires model+channel+
  // rates, so we resend the existing row with `active` flipped.
  const toggleActive = useCallback(async (row) => {
    setTogglingId(row._id);
    setError(null);
    try {
      const body = {
        model: row.model,
        channel: row.channel,
        rates: row.rates,
        verified: row.verified,
        active: !row.active,
      };
      if (row.notes) body.notes = row.notes;
      if (row.source_url) body.source_url = row.source_url;
      if (row.effective_date) body.effective_date = row.effective_date.slice(0, 10);
      await upsertCostPricing(body);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update status.");
    } finally {
      setTogglingId(null);
    }
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => data?.pricing || [], [data]);

  return (
    <div className="space-y-6">
      <Panel
        title="Model pricing"
        right={
          <Button size="sm" onClick={() => setDialog({ row: null })}>
            <Plus className="mr-1.5 h-4 w-4" /> Add model
          </Button>
        }
      >
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No pricing rows yet. Add a model to start pricing usage.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Model</th>
                  <th className="py-2 px-3 font-medium">Channel</th>
                  <th className="py-2 px-3 font-medium">Rates (USD / 1M)</th>
                  <th className="py-2 px-3 font-medium">Effective</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 pl-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} className="border-b align-top last:border-0">
                    <td className="py-2.5 pr-3 font-mono text-xs">
                      <div>{r.model}</div>
                      {r.notes ? (
                        <div className="mt-0.5 text-[11px] font-sans text-muted-foreground">
                          {r.notes}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2.5 px-3">
                      <ChannelBadge channel={r.channel} />
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                        {Object.entries(r.rates || {}).map(([k, v]) => (
                          <span key={k} className="tabular-nums">
                            <span className="text-muted-foreground">{k}</span>{" "}
                            {fmtRate(v)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-muted-foreground">
                      {fmtDateTime(r.effective_date)}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex flex-col gap-2">
                        {r.verified ? (
                          <Badge
                            variant="outline"
                            className="w-fit border-emerald-300 bg-emerald-50 text-emerald-700"
                          >
                            <CheckCircle2 className="mr-1 h-3 w-3" /> verified
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="w-fit border-amber-300 bg-amber-50 text-amber-700"
                          >
                            estimated
                          </Badge>
                        )}
                        <label className="flex items-center gap-2 text-xs">
                          <Switch
                            checked={!!r.active}
                            disabled={togglingId === r._id}
                            onCheckedChange={() => toggleActive(r)}
                          />
                          <span
                            className={
                              r.active ? "text-foreground" : "text-muted-foreground"
                            }
                          >
                            {togglingId === r._id
                              ? "Saving…"
                              : r.active
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </label>
                      </div>
                    </td>
                    <td className="py-2.5 pl-3 text-right">
                      <button
                        type="button"
                        onClick={() => setDialog({ row: r })}
                        className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <PricingDialog
        open={!!dialog}
        row={dialog?.row || null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          load();
        }}
      />
    </div>
  );
};

export default PricingTab;
